import { ChangeEvent, MouseEvent, MutableRefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormControlLabel, List, Radio, RadioGroup } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Checkbox from '@mui/joy/Checkbox';
import moment from 'moment';
import InputBase from 'components/Common/InputBase';
import {
    FILTER_LAST_3_MONTHS,
    FILTER_LAST_6_MONTHS,
    FormattedObjectType,
    Inputs,
    JobsTypes,
    TabsType,
} from 'components/Sidebar/types';
import { makeShort } from 'helpers/strings';
import { SearchHistoryType } from 'store/global/types';
import useDebounce from 'hooks/useDebounce';
import SidebarItem from '../SidebarItem';

type SearchResultsProps = {
    activeTab: TabsType;
    searchHistory: SearchHistoryType[];
    searchedSavedValues: Inputs | null;
    searchedValue: FormattedObjectType[];
    savedUnFormattedData: MutableRefObject<JobsTypes[]>;
    onSearch: ({ searchValue, isBookmarkSelected, monthFilter }: Inputs) => void;
    addSearchHistory: (title: string, subtitle: string, job_id: string) => void;
    closeBar: () => void;
    onOpenROneModal: (jobOrderId?: string) => void;
};

export const SearchResults = ({
    activeTab,
    savedUnFormattedData,
    searchHistory,
    searchedValue,
    searchedSavedValues,
    onSearch,
    addSearchHistory,
    closeBar,
    onOpenROneModal,
}: SearchResultsProps) => {
    const { t } = useTranslation();

    const [searchValue, setSearchValue] = useState<string>(searchedSavedValues?.searchValue || '');
    const [isBookmarkSelected, setIsBookmarkSelected] = useState<boolean>(
        !!searchedSavedValues?.isBookmarkSelected,
    );
    const [monthFilter, setMonthFilter] = useState<string>(searchedSavedValues?.monthFilter ?? '');
    const [isFirstOne, setIsFirstOne] = useState<boolean>(false);
    const debounced = useDebounce<string>(searchValue, 300);

    useEffect(() => {
        onSearch({
            searchValue: debounced ?? '',
            isBookmarkSelected,
            monthFilter,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const showDate = (value: string) => {
        const date = new Date(value);
        return moment(date).format('ll');
    };

    const onHistoryItemClick = (value: string) => {
        setSearchValue(value);
    };

    const onEmpty = () => {
        setSearchValue('');
    };

    const onSaveHistory = (title: string, subtitle: string, job_id: string) => {
        if (isFirstOne) {
            addSearchHistory(title, subtitle, job_id);
            setIsFirstOne(false);
        }
    };

    const showValue = (value: string) => {
        if (!searchValue) {
            return value;
        }
        const splitString = searchValue.split(' ');
        const replacement = '<strong>$&</strong>';

        splitString.forEach((word) => {
            value = value.replace(new RegExp(`${word}(?![^<]*>)`, 'i'), replacement);
        });

        const shownValue = <div dangerouslySetInnerHTML={{ __html: value }}></div>;
        return shownValue;
    };

    const onToggleBookmark = () => {
        setIsFirstOne(true);
        onSearch({ searchValue, isBookmarkSelected: !isBookmarkSelected, monthFilter });
        setIsBookmarkSelected((prev) => !prev);
    };

    const handleMonthFilter = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setIsFirstOne(true);
        onSearch({ searchValue, isBookmarkSelected, monthFilter: value });
        setMonthFilter((prev) => (prev !== value ? value : ''));
    };

    const isShowResponse = searchValue || isBookmarkSelected || monthFilter;

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setIsFirstOne(true);
        setSearchValue(e.target.value);
    };

    const handleClickRadio = (e: MouseEvent<HTMLInputElement>) => {
        const target = e?.target as HTMLInputElement;
        if (target?.nodeName === 'INPUT' && monthFilter === target.value) {
            setMonthFilter('');
        }
    };

    const showSuggestion = () => {
        if (!searchValue) {
            return '';
        }
        const values = savedUnFormattedData?.current;

        // find first one from list which starts with searched Value
        const suggestedItem = values?.find(
            (x) =>
                x?.title?.toLowerCase()?.startsWith(searchValue.toLowerCase()) ||
                x?.subtitle?.toLowerCase()?.startsWith(searchValue.toLowerCase()),
        );

        // check title or subtitle
        const suggestion = suggestedItem?.title
            ?.toLowerCase()
            ?.startsWith(searchValue.toLowerCase())
            ? suggestedItem?.title
            : suggestedItem?.subtitle;

        // replace suggestion part what included in search (here can be mismatching with uppercase)
        const searchedValueLength = searchValue.length;
        const replacedPart = suggestion?.slice(0, searchedValueLength) ?? '';
        const replacedValue = suggestion?.replace(replacedPart, searchValue);
        return replacedValue ?? '';
    };

    const getNoSearchResultsMessage = (translation: string, searchValue: string) => {
        return translation.replace('{search_value}', `"${searchValue}" `);
    };

    return (
        <Box className="sidebar-actions-search-content">
            <Box className="search-input">
                <InputBase
                    type="text"
                    name="searchValue"
                    value={searchValue}
                    onChangeInput={onChangeSearch}
                    placeholder={t('web_app.tabs.search_placeholder')}
                />
                <SearchOutlinedIcon className="search-input-icon" />
                <button className="search-input-clear" onClick={onEmpty}>
                    <CloseIcon />
                </button>
                <Box className="search-input-suggestion">
                    <InputBase
                        type="text"
                        name="searchValuePlaceholder"
                        value={showSuggestion()}
                        disabled
                    />
                </Box>
            </Box>
            <Box className="search-actions-content">
                <Box className="search-actions-content-filters">
                    <FormControlLabel
                        className="filters-components"
                        onChange={onToggleBookmark}
                        control={
                            <Checkbox className="filters-components-checkbox" variant="outlined" />
                        }
                        checked={isBookmarkSelected}
                        label={t('web_app.search.filters.bookmark')}
                    />
                    <FormControl>
                        <RadioGroup
                            value={monthFilter}
                            defaultValue={monthFilter}
                            className="filters-components"
                            onChange={handleMonthFilter}
                            onClick={handleClickRadio}
                        >
                            <FormControlLabel
                                value={FILTER_LAST_3_MONTHS}
                                control={<Radio />}
                                label={t('web_app.search.filters.last_3_months')}
                            />
                            <FormControlLabel
                                value={FILTER_LAST_6_MONTHS}
                                control={<Radio />}
                                label={t('web_app.search.filters.last_6_months')}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </Box>
            <Box className="search-actions-results">
                {isShowResponse ? (
                    <>
                        <Box className="search-actions-results-values">
                            <Box>
                                {t('web_app.search.contains')}{' '}
                                <strong>{makeShort(searchValue, 40)}</strong>
                            </Box>
                        </Box>
                        <Box className="search-actions-results-searched-values">
                            {searchedValue.map((context) => (
                                <Box className="history-item" key={context.id}>
                                    <Box className="flex">
                                        <Box className="month">{context.id}</Box>
                                        <Box> {context.year}</Box>
                                    </Box>
                                    <List>
                                        {context.items.map((state) => (
                                            <SidebarItem
                                                item={state}
                                                key={`${activeTab}-${state.document_id}`}
                                                closeBar={closeBar}
                                                showValue={showValue}
                                                searchValue={debounced}
                                                onSaveHistory={onSaveHistory}
                                                onOpenROneModal={onOpenROneModal}
                                            />
                                        ))}
                                    </List>
                                </Box>
                            ))}
                            {searchedValue?.length === 0 && (
                                <Box className="search-actions-results-empty">
                                    {getNoSearchResultsMessage(
                                        t('web_app.search.empty'),
                                        searchValue,
                                    )}
                                </Box>
                            )}
                        </Box>
                    </>
                ) : (
                    <>
                        <Box className="search-actions-results-values">
                            <Box>{t('web_app.search.history_title')}</Box>
                        </Box>
                        {searchHistory.length === 0 && (
                            <Box className="search-actions-results-searched-history-empty">
                                {t('web_app.search.history_empty')}
                            </Box>
                        )}
                        <Box className="search-actions-results-searched-history">
                            <ul>
                                {searchHistory?.map((item) => (
                                    <li key={item.document_id}>
                                        <Box>
                                            <AccessTimeOutlinedIcon />
                                            <Box>
                                                <button
                                                    onClick={() =>
                                                        onHistoryItemClick(
                                                            item.title + ' ' + item.subtitle,
                                                        )
                                                    }
                                                    title={item.title}
                                                >
                                                    <Box title={item.title}>
                                                        {makeShort(item.title, 40)}
                                                    </Box>
                                                    <Box title={item.subtitle}>
                                                        {makeShort(item.subtitle, 40)}
                                                    </Box>
                                                </button>
                                            </Box>
                                        </Box>
                                        <Box>{showDate(item.created_at)}</Box>
                                    </li>
                                ))}
                            </ul>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
